import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTrailById, getTrailContent, deleteLearningTrail } from '../../services/learningTrailService';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import TrailContentSection from './components/TrailContentSection';
import TrailAnalytics from './components/TrailAnalytics';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';

const LearningTrailDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [trail, setTrail] = useState(null);
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchTrailData();
  }, [id]);

  const fetchTrailData = async () => {
    setLoading(true);
    setError(null);

    const { trail: trailData, error: trailError } = await getTrailById(id);
    if (trailError) {
      setError(trailError);
      setLoading(false);
      return;
    }

    const { contents: contentData, error: contentError } = await getTrailContent(id);
    if (contentError) {
      setError(contentError);
      setLoading(false);
      return;
    }

    // Transform content data to ensure proper field mapping
    const transformedContent = contentData?.map(item => {
      if (item?.content_type === 'quiz' && item?.content_data) {
        // Map database field names (snake_case) to component field names (camelCase)
        const contentData = { ...item?.content_data };
        if (contentData?.correct_answer !== undefined) {
          contentData.correctAnswer = contentData?.correct_answer;
        }
        return {
          ...item,
          content_data: contentData
        };
      }
      return item;
    }) || [];

    setTrail(trailData);
    setContents(transformedContent);
    setLoading(false);
  };

  const handleEdit = () => {
    navigate('/learning-trails-creator', { state: { mode: 'edit', journeyId: id } });
  };

  const handleDelete = async () => {
    setDeleting(true);
    const { success, error: deleteError } = await deleteLearningTrail(id);
    
    if (success) {
      navigate('/teacher-dashboard');
    } else {
      setError(deleteError);
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleDuplicate = () => {
    // Future implementation for duplicating trails
    console.log('Duplicate trail:', id);
  };

  const handleGenerateQR = () => {
    navigate('/qr-code-generator', { state: { trailId: id } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading trail...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !trail) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Trail Not Found</h2>
            <p className="text-muted-foreground mb-6">{error || 'The requested trail could not be found.'}</p>
            <Button onClick={() => navigate('/teacher-dashboard')}>Back to Dashboard</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Breadcrumb Navigation */}
      <div className="bg-card border-b border-border">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center space-x-2 text-sm">
            <button
              onClick={() => navigate('/teacher-dashboard')}
              className="text-primary hover:underline flex items-center"
            >
              <Icon name="Home" size={16} className="mr-1" />
              Dashboard
            </button>
            <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
            <span className="text-foreground font-medium">Trail Details</span>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content Area */}
          <div className="lg:col-span-2 space-y-6">
            {/* Trail Header */}
            <div className="bg-card rounded-lg shadow-sm overflow-hidden">
              {trail?.image_url && (
                <div className="relative h-64 w-full">
                  <Image
                    src={trail?.image_url}
                    alt={trail?.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h1 className="text-3xl font-bold text-foreground">{trail?.title}</h1>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        trail?.published 
                          ? 'bg-success/10 text-success' :'bg-warning/10 text-warning'
                      }`}>
                        {trail?.published ? 'Published' : 'Draft'}
                      </span>
                    </div>
                    <p className="text-muted-foreground">{trail?.description}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-6 text-sm text-muted-foreground">
                  <div className="flex items-center">
                    <Icon name="Users" size={16} className="mr-2 text-primary" />
                    <span>{trail?.classrooms?.classroom_name || 'No classroom'}</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Calendar" size={16} className="mr-2 text-primary" />
                    <span>Created {new Date(trail?.created_at)?.toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Trail Content Sections */}
            <div className="bg-card rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center">
                <Icon name="BookOpen" size={20} className="mr-2 text-primary" />
                Trail Content
              </h2>
              
              {contents?.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No content added yet</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={handleEdit}
                  >
                    Add Content
                  </Button>
                </div>
              ) : (
                <TrailContentSection contents={contents} />
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Action Toolbar */}
            <div className="bg-card rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Actions</h3>
              <div className="space-y-3">
                <Button
                  variant="default"
                  className="w-full"
                  iconName="Edit"
                  onClick={handleEdit}
                >
                  Edit Trail
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  iconName="Copy"
                  onClick={handleDuplicate}
                >
                  Duplicate Trail
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  iconName="QrCode"
                  onClick={handleGenerateQR}
                >
                  Generate QR Code
                </Button>
                <Button
                  variant="outline"
                  className="w-full text-error hover:bg-error/10"
                  iconName="Trash2"
                  onClick={() => setShowDeleteModal(true)}
                >
                  Delete Trail
                </Button>
              </div>
            </div>

            {/* Analytics Panel */}
            <TrailAnalytics trailId={id} />
          </div>
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        trailTitle={trail?.title}
        isDeleting={deleting}
      />
    </div>
  );
};

export default LearningTrailDetailView;